// ModalAuth.jsx
import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaGoogle, FaTimes } from "react-icons/fa";
import { auth, db } from "../firebase/firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom"; // ✅ Added
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";

// --- styled-components --- //
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  padding: 2rem;
  width: 400px;
  position: relative;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 480px) {
    width: 90%;
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.7rem;
  background: transparent;
  border: none;
  color: ${({ active }) => (active ? "#ff7a18" : "#ccc")};
  font-size: 1rem;
  cursor: pointer;
  border-bottom: ${({ active }) =>
    active ? "2px solid #ff7a18" : "2px solid transparent"};
`;

const InputField = styled(Field)`
  width: 95%;
  height: 40px;
  margin-top: 0.5rem;
  border-radius: 8px;
  border: none;
  outline: none;
  margin-bottom: 0.3rem;
  padding: 0 10px;
`;

const ErrorText = styled.div`
  color: #ff4d4d;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.9rem;
  border-radius: 8px;
  border: none;
  background: #ff7a18;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 0.8rem;
  display: flex;
  justify-content: center;
  align-items: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Loader = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: #aaa;
  font-size: 0.9rem;
  margin: 1rem 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }
  &::before {
    margin-right: 0.75em;
  }
  &::after {
    margin-left: 0.75em;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.9rem;
  border-radius: 8px;
  border: none;
  background: #db4437;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    font-size: 1.2rem;
  }
`;

const CountrySelect = styled(Field)`
  width: 100px;
  height: 40px;
  margin-top: 0.5rem;
  margin-right: 8px;
  border-radius: 8px;
  border: none;
  outline: none;
  margin-bottom: 0.3rem;
  padding: 0 5px;
`;

const ForgotPassword = styled.div`
  text-align: right;
  margin-top: 0.6rem;
  font-size: 0.9rem;

  a {
    color: #ff7a18;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// src/utils/firebaseErrors.js
export const getAuthErrorMessage = (code) => {
  switch (code) {
    case "auth/wrong-password":
      return "❌ Current password is incorrect.";
    case "auth/weak-password":
      return "⚠️ New password is too weak (min 6 characters).";
    case "auth/invalid-credential":
      return "❌ Invalid credentials. Please try again.";
    case "auth/too-many-requests":
      return "🚫 Too many failed attempts. Try again later.";
    case "auth/network-request-failed":
      return "🌐 Network error. Check your internet connection.";
    case "auth/requires-recent-login":
      return "🔒 Please log in again before changing your password.";
    default:
      return "⚠️ Something went wrong. Please try again.";
  }
};

// ✅ Validation
const validationSchemas = {
  login: Yup.object({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(6, "Minimum 6 characters")
      .required("Password is required"),
  }),
  signup: Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(/^\d{7,14}$/, "Enter valid mobile number")
      .required("Mobile number is required"),
    password: Yup.string()
      .min(6, "Minimum 6 characters")
      .required("Password is required"),
    countryCode: Yup.string().required("Select country code"),
  }),
};

const countryCodes = [
  { code: "+91", country: "India" },
  { code: "+1", country: "USA" },
  { code: "+44", country: "UK" },
  { code: "+61", country: "Australia" },
];

const ModalAuth = ({ onClose, isOpen }) => {
  const { setUser } = useContext(UserContext);
  const [tab, setTab] = useState("login");
  const navigate = useNavigate(); // ✅ Added navigation hook

  if (!isOpen) return null;

  // 🔹 Google login
  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      toast.success("Google Login Successful!");
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        <Tabs>
          <Tab active={tab === "login"} onClick={() => setTab("login")}>
            Login
          </Tab>
          <Tab active={tab === "signup"} onClick={() => setTab("signup")}>
            Sign Up
          </Tab>
        </Tabs>

        <Formik
          enableReinitialize
          initialValues={
            tab === "login"
              ? { email: "", password: "" }
              : {
                  name: "",
                  email: "",
                  countryCode: "+91",
                  mobile: "",
                  password: "",
                }
          }
          validationSchema={validationSchemas[tab]}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (tab === "login") {
                // 🔹 Login
                const userCred = await signInWithEmailAndPassword(
                  auth,
                  values.email,
                  values.password
                );

                const user = userCred.user;
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  setUser({
                    id: user.uid,
                    name: userData.name,
                    email: userData.email,
                    role: userData.role,
                  });
                } else {
                  setUser({
                    id: user.uid,
                    name: "User",
                    email: user.email,
                  });
                }

                toast.success("Login successful!");
                onClose();
              } else {
                // 🔹 Signup
                const userCred = await createUserWithEmailAndPassword(
                  auth,
                  values.email,
                  values.password
                );

                const user = userCred.user;
                await updateProfile(userCred.user, {
                  displayName: values.name,
                });

                await setDoc(doc(db, "users", userCred.user.uid), {
                  name: values.name,
                  email: values.email,
                  mobile: `${values.countryCode}${values.mobile}`,
                  id: user.uid,
                  role: "user",
                });

                setUser({
                  name: values.name,
                  email: values.email,
                  mobile: `${values.countryCode}${values.mobile}`,
                  id: user.uid,
                  role: "user",
                });

                toast.success("Account created successfully!");
                onClose();
              }
            } catch (err) {
              toast.error(getAuthErrorMessage(err.code));
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              {tab === "signup" && (
                <>
                  <label>Name</label>
                  <InputField name="name" placeholder="Your Name" />
                  <ErrorMessage name="name" component={ErrorText} />

                  <label>Mobile Number</label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <CountrySelect as="select" name="countryCode">
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.country} ({c.code})
                        </option>
                      ))}
                    </CountrySelect>
                    <InputField name="mobile" placeholder="9876543210" />
                  </div>
                  <ErrorMessage name="mobile" component={ErrorText} />
                </>
              )}

              <label>Email</label>
              <InputField name="email" placeholder="Your Email" />
              <ErrorMessage name="email" component={ErrorText} />

              <label>Password</label>
              <InputField
                name="password"
                type="password"
                placeholder="Your Password"
              />
              <ErrorMessage name="password" component={ErrorText} />

              {/* ✅ Forgot Password Link */}
              {tab === "login" && (
                <ForgotPassword>
                  <a
                    onClick={() => {
                      onClose();
                      navigate("/forgot-password");
                    }}
                  >
                    Forgot Password?
                  </a>
                </ForgotPassword>
              )}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader />
                ) : tab === "login" ? (
                  "Login"
                ) : (
                  "Sign Up"
                )}
              </SubmitButton>

              <Divider>Or</Divider>

              <GoogleButton type="button" onClick={handleGoogleLogin}>
                <FaGoogle /> Continue with Google
              </GoogleButton>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModalAuth;
